<template>
  <div>
    <div v-if="history && history.length > 0">
      <div class="table-responsive">
        <table class="table table-centered table-bordered table-nowrap mb-0 table-hover">
          <thead class="thead-light">
            <tr>
              <th style="width: 150px">{{ $t("audit-control.history.date_user") }}</th>
              <th>{{ $t("audit-control.history.action.text") }}</th>
              <th>{{ $t("audit-control.test.status.text") }}</th>
              <th>{{ $t("audit-control.test.result.text") }}</th>
              <th>{{ $t("audit-control.test.performed-by") }}</th>
              <th>{{ $t("audit-control.test.start-date") }}</th>
              <th>{{ $t("audit-control.test.completion-date") }}</th>
              <th>{{ $t("common.note") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedHistory" :key="item.date">
              <td>
                <small class="d-block text-muted">{{ item.date | date }}</small>
                <span class="font-weight-bold">{{ item.performedBy.name }} {{ item.performedBy.surname }}</span>
              </td>
              <td>
                <b-badge :variant="getActionVariant(item.action)">{{ $t(`audit-control.history.action.${item.action}`) }}</b-badge>
                <div v-if="item.description" class="small text-muted mt-1">{{ item.description }}</div>
                
                <!-- Detailed changes loop -->
                <div v-if="item.changes && Object.keys(item.changes).length > 0" class="mt-2 text-wrap" style="max-width: 300px;">
                  <div v-for="(change, key) in item.changes" :key="key" class="font-size-12 mb-1 border-left pl-2">
                    <span class="text-muted font-weight-bold mr-1">{{ getFieldLabel(key) }}:</span>
                    <span class="text-danger strike-through">{{ formatValue(change.old, key) }}</span>
                    <span class="mx-1 text-muted"><i class="bx bx-right-arrow-alt"></i></span>
                    <span class="text-success">{{ formatValue(change.new, key) }}</span>
                  </div>
                </div>
              </td>

              <!-- Snapshot Fields with Highlighting -->
              <td :class="{ 'bg-soft-warning': isChanged(item, 'status') }">
                <b-badge v-if="getSnapshotValue(item, 'status')" :variant="getStatusVariant(getSnapshotValue(item, 'status'))">
                  {{ $t(`audit-control.test.status.${getSnapshotValue(item, "status").toLowerCase()}`) }}
                </b-badge>
                <span v-else>-</span>
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'result') }">
                <b-badge v-if="getSnapshotValue(item, 'result')" :variant="getResultVariant(getSnapshotValue(item, 'result'))">
                  {{ $t(`audit-control.test.result.${getSnapshotValue(item, "result").toLowerCase()}`) }}
                </b-badge>
                <span v-else>-</span>
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'performedBy') }">
                {{ formatPerformedBy(getSnapshotValue(item, "performedBy")) }}
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'startDate') }">
                {{ getSnapshotValue(item, "startDate") | date }}
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'completionDate') }">
                {{ getSnapshotValue(item, "completionDate") | date }}
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'note') }">
                <span v-b-tooltip.hover :title="getLocalizedValue(getSnapshotValue(item, 'note'))" class="d-inline-block text-truncate" style="max-width: 150px;">
                  {{ getLocalizedValue(getSnapshotValue(item, "note")) || "-" }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="row mt-4">
        <div class="col">
          <b-pagination v-model="currentPage" :total-rows="history.length" :per-page="perPage" limit="10" size="sm"></b-pagination>
        </div>
      </div>
    </div>
    <empty v-else>
      <h5 class="text-center">{{ $t("common.no-data") }}</h5>
    </empty>
  </div>
</template>

<script>
export default {
  props: {
    history: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    paginatedHistory() {
      // Sort desc by date
      const sorted = [...this.history].sort((a, b) => new Date(b.date) - new Date(a.date));
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return sorted.slice(start, end);
    },
  },
  methods: {
    getSnapshotValue(item, field) {
      if (!item.snapshot) return null;
      return item.snapshot[field];
    },
    isChanged(item, field) {
      return item.changes && item.changes[field] !== undefined;
    },
    getLocalizedValue(val) {
      if (!val) return "";
      if (typeof val !== "object") return val;
      return val[this.$i18n.locale] || val.en || val.tr || "";
    },
    formatPerformedBy(val) {
      if (!val) return "-";
      return `${val.name}`;
    },
    getFieldLabel(key) {
      const map = {
        name: this.$t("audit-control.test.name") || "Adı",
        status: this.$t("audit-control.test.status.text"),
        result: this.$t("audit-control.test.result.text"),
        performedBy: this.$t("audit-control.test.performed-by"),
        startDate: this.$t("audit-control.test.start-date"),
        completionDate: this.$t("audit-control.test.completion-date"),
        note: this.$t("common.note"),
      };
      return map[key] || key;
    },
    formatValue(val, key) {
      if (val === null || val === undefined) return "-";
      
      if (typeof val === "string") {
        const isEnum = /^[A-Z0-9_]+$/.test(val);
        if (isEnum) {
          const enumKey = val.toLowerCase().replace(/_/g, '-');
          if (key === "status") return this.$te(`audit-control.test.status.${enumKey}`) ? this.$t(`audit-control.test.status.${enumKey}`) : val;
          if (key === "result") return this.$te(`audit-control.test.result.${enumKey}`) ? this.$t(`audit-control.test.result.${enumKey}`) : val;
        }
        if (key === "startDate" || key === "completionDate") return new Date(val).toLocaleDateString();
        return val;
      }

      if (Array.isArray(val)) {
        if (val.length === 0) return "[]";
        return val.map(v => {
          if (typeof v === 'object' && v !== null) {
            if (v.name) return this.getLocalizedValue(v.name);
            if (v.no) return v.no;
            if (v.tr || v.en || v[this.currentLanguage]) return v[this.currentLanguage] || v.tr || v.en;
            if (v._id) return v._id;
          }
          return v;
        }).join(", ");
      }

      if (typeof val === "object" && val !== null) {
        if (val.tr || val.en || val[this.currentLanguage]) {
          return val[this.currentLanguage] || val.tr || val.en;
        }
        if (val.name) return this.getLocalizedValue(val.name);
        if (val instanceof Date) return val.toLocaleDateString();
        return JSON.stringify(val);
      }
      return val;
    },
    getActionVariant(action) {
      switch (action) {
        case "CREATED":
          return "success";
        case "UPDATED":
          return "info";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        case "WAITING_FOR_APPROVAL":
          return "warning";
        default:
          return "primary";
      }
    },
    getStatusVariant(status) {
      switch (status) {
        case "ACTIVE":
          return "primary";
        case "PARTIALLY_COMPLETED":
          return "warning";
        case "COMPLETED":
          return "success";
        default:
          return "secondary";
      }
    },
    getResultVariant(result) {
      switch (result) {
        case "EFFECTIVE":
          return "success";
        case "NOT_EFFECTIVE":
          return "danger";
        default:
          return "light";
      }
    },
  },
};
</script>
